import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

export const useModelTooltip = ({ map, enabled = true }) => {
  const pointsOnlyMode = useSelector(state => state.mapState.pointsOnlyMode)
  const tooltipRef = useRef(null)
  const currentFeatureIdRef = useRef(null)
  const isMapMovingRef = useRef(false)

  useEffect(() => {
    if (!map || !enabled) return

    const hideTooltip = tooltip => {
      tooltip.style.display = 'none'
      currentFeatureIdRef.current = null
    }

    const escapeHtml = value =>
      String(value ?? '').replace(/[&<>"']/g, char => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }
        return entities[char]
      })

    const parseTooltipData = rawTooltip => {
      if (!rawTooltip) return {}
      if (typeof rawTooltip === 'object') return rawTooltip

      try {
        return JSON.parse(rawTooltip) || {}
      } catch {
        return {}
      }
    }

    const formatMetric = (value, unit) => {
      if (value === null || value === undefined || value === '') return '&mdash;'
      return `${escapeHtml(value)} ${unit}`
    }

    const getFeatureKey = props => props?.id ?? props?.code ?? null

    const getInteractiveLayers = () => {
      if (pointsOnlyMode) {
        return map.getLayer('unclustered-point') ? ['unclustered-point'] : []
      }

      const layers = map.getStyle()?.layers || []
      return layers
        .filter(layer => layer.id.startsWith('trees-3d-'))
        .map(layer => layer.id)
    }

    const layerIds = getInteractiveLayers()
    if (layerIds.length === 0) return

    if (!tooltipRef.current) {
      const tooltip = document.createElement('div')
      tooltip.className = 'model-tooltip'
      tooltip.style.display = 'none'
      document.body.appendChild(tooltip)
      tooltipRef.current = tooltip
    }

    const tooltip = tooltipRef.current

    const updateTooltipContent = props => {
      const tooltipData = parseTooltipData(props.tooltip)

      const pseudonym = tooltipData.pseudonym
      const specie = tooltipData.specie || 'Especie no registrada'
      const code = props.code
      const displayName = escapeHtml(pseudonym || specie)
      const scientificName = escapeHtml(specie)
      const codeMarkup = code
        ? `
          <div class="model-tooltip__code-chip">
            <strong>${escapeHtml(code)}</strong>
          </div>
        `
        : ''

      tooltip.innerHTML = `
        <div class="model-tooltip__container">
          <div class="model-tooltip__species-block">
            ${codeMarkup}
            <div class="model-tooltip__species-name">${displayName}</div>
            ${
              pseudonym
                ? `<div class="model-tooltip__species-scientific">${scientificName}</div>`
                : ''
            }
          </div>
          
          <div class="model-tooltip__metrics-row">
            <div class="model-tooltip__metric-block">
              <div class="model-tooltip__metric-info">
                <span class="model-tooltip__label">Altura</span>
                <span class="model-tooltip__value">${formatMetric(props.ht, 'm')}</span>
              </div>
            </div>

            <div class="model-tooltip__metric-block">
              <div class="model-tooltip__metric-info">
                <span class="model-tooltip__label">Diámetro</span>
                <span class="model-tooltip__value">${formatMetric(props.dap, 'cm')}</span>
              </div>
            </div>
          </div>
        </div>
      `
    }

    const handleMouseEnter = e => {
      if (!e.features?.length || isMapMovingRef.current) return

      const feature = e.features[0]
      currentFeatureIdRef.current = getFeatureKey(feature.properties)
      updateTooltipContent(feature.properties)
      tooltip.style.display = 'block'
    }

    const handleMouseMove = e => {
      if (isMapMovingRef.current) {
        if (tooltip.style.display !== 'none') {
          hideTooltip(tooltip)
        }
        return
      }

      try {
        const features = map.queryRenderedFeatures(e.point, { layers: layerIds })

        if (features.length > 0) {
          const feature = features[0]
          const props = feature.properties
          const featureKey = getFeatureKey(props)

          if (currentFeatureIdRef.current !== featureKey) {
            currentFeatureIdRef.current = featureKey
            updateTooltipContent(props)
          }

          if (tooltip.style.display === 'none') {
            tooltip.style.display = 'block'
          }

          const offset = 16
          const padding = 10
          const tooltipRect = tooltip.getBoundingClientRect()
          const canvasRect = map.getCanvas().getBoundingClientRect()

          let x = canvasRect.left + e.point.x + offset
          let y = canvasRect.top + e.point.y + offset

          if (x + tooltipRect.width + padding > window.innerWidth) {
            x = canvasRect.left + e.point.x - tooltipRect.width - offset
          }

          if (y + tooltipRect.height + padding > window.innerHeight) {
            y = canvasRect.top + e.point.y - tooltipRect.height - offset
          }

          tooltip.style.left = `${x}px`
          tooltip.style.top = `${y}px`
        } else {
          hideTooltip(tooltip)
        }
      } catch (error) {
        console.warn('[useModelTooltip] Error consultando capas interactivas')
        hideTooltip(tooltip)
      }
    }

    const handleMouseLeave = () => {
      hideTooltip(tooltip)
    }

    const handleMapMoveStart = () => {
      isMapMovingRef.current = true
      hideTooltip(tooltip)
    }

    const handleMapMoveEnd = () => {
      isMapMovingRef.current = false
    }

    map.on('movestart', handleMapMoveStart)
    map.on('dragstart', handleMapMoveStart)
    map.on('pitchstart', handleMapMoveStart)
    map.on('rotatestart', handleMapMoveStart)
    map.on('zoomstart', handleMapMoveStart)
    map.on('moveend', handleMapMoveEnd)
    map.on('zoomend', handleMapMoveEnd)
    map.on('mousemove', handleMouseMove)

    // Registrar eventos sobre modelos 3D o puntos, según el modo activo.
    layerIds.forEach(id => {
      map.on('mouseenter', id, handleMouseEnter)
      map.on('mouseleave', id, handleMouseLeave)
    })

    return () => {
      map.off('movestart', handleMapMoveStart)
      map.off('dragstart', handleMapMoveStart)
      map.off('pitchstart', handleMapMoveStart)
      map.off('rotatestart', handleMapMoveStart)
      map.off('zoomstart', handleMapMoveStart)
      map.off('moveend', handleMapMoveEnd)
      map.off('zoomend', handleMapMoveEnd)
      map.off('mousemove', handleMouseMove)

      layerIds.forEach(id => {
        map.off('mouseenter', id, handleMouseEnter)
        map.off('mouseleave', id, handleMouseLeave)
      })

      if (tooltip?.parentNode) {
        tooltip.parentNode.removeChild(tooltip)
      }
      tooltipRef.current = null
    }
  }, [map, enabled, pointsOnlyMode])
}

export default useModelTooltip
